const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

interface ApiResponse<T> {
  data?: T
  error?: string
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    })

    const data = await response.json()

    if (!response.ok) {
      return { error: data.error || 'Something went wrong' }
    }

    return { data }
  } catch (error) {
    console.error('API error:', error)
    return { error: 'Unable to connect to server' }
  }
}

// Competition
export async function getCompetitionStatus() {
  return request<{
    phase: string
    name: string
    votingMode: 'classic' | 'elo'
    submissionDeadline?: string
    votingDeadline?: string
  }>('/api/competition/status')
}

// ============ Submissions (No Magic Link) ============

export async function checkSubmissionEligibility(email: string) {
  return request<{
    eligible: boolean
    email: string
    previouslyRejected?: boolean
    rejectionReason?: string
    hasDraft?: boolean
    draftStep?: number
  }>('/api/submissions/check-eligibility', {
    method: 'POST',
    body: JSON.stringify({ email }),
  })
}

export interface SubmitPhotoInput {
  email: string
  name: string
  grade: string
  photoTitle: string
  contentType: string
  guidelinesAcceptedAt?: string
  includesPeople?: boolean
  hasModelPermission?: boolean
  // Optional: if photo was already uploaded via optimistic upload
  photoS3Key?: string
}

export interface SubmitPhotoResponse {
  submissionId: string
  // uploadUrl is optional if photo was pre-uploaded via optimistic upload
  uploadUrl?: string
  expiresIn?: number
}

export async function submitPhoto(input: SubmitPhotoInput) {
  return request<SubmitPhotoResponse>('/api/submissions', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function uploadPhotoToS3(uploadUrl: string, file: File): Promise<boolean> {
  try {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    })
    return response.ok
  } catch (error) {
    console.error('S3 upload error:', error)
    return false
  }
}

// ============ Optimistic Upload ============

export interface PresignedUploadResponse {
  uploadUrl: string
  s3Key: string
  expiresIn: number
}

export async function requestPresignedUrl(email: string, contentType: string, tempId: string) {
  return request<PresignedUploadResponse>('/api/submissions/presign-upload', {
    method: 'POST',
    body: JSON.stringify({ email, contentType, tempId }),
  })
}

// ============ Draft Submissions ============

export interface DraftSubmission {
  id: string
  competitionId: string
  email: string
  name?: string
  grade?: string
  photoTitle?: string
  photoS3Key?: string
  photoUrl?: string
  status: 'draft'
  guidelinesAcceptedAt?: string
  includesPeople?: boolean
  hasModelPermission?: boolean
  createdAt: string
  updatedAt: string
  draftStep: number
}

export interface SaveDraftInput {
  email: string
  draftStep: number
  name?: string
  grade?: string
  photoTitle?: string
  photoS3Key?: string
  guidelinesAcceptedAt?: string
  includesPeople?: boolean
  hasModelPermission?: boolean
}

export async function getDraft(email: string) {
  return request<{ draft: DraftSubmission }>(`/api/submissions/draft?email=${encodeURIComponent(email)}`)
}

export async function saveDraft(input: SaveDraftInput) {
  return request<{ draft: DraftSubmission }>('/api/submissions/draft', {
    method: 'PUT',
    body: JSON.stringify(input),
  })
}

// ============ Voting (No Magic Link) ============

export interface Photo {
  id: string
  photoTitle: string
  name: string
  grade: string
  photoUrl: string
}

export async function checkVoteEligibility(email: string) {
  return request<{
    eligible: boolean
    email: string
    photos: Photo[]
  }>('/api/vote/check-eligibility', {
    method: 'POST',
    body: JSON.stringify({ email }),
  })
}

export async function castVote(email: string, firstChoice: string, secondChoice: string, thirdChoice: string) {
  return request<{ message: string }>('/api/vote', {
    method: 'POST',
    body: JSON.stringify({ email, firstChoice, secondChoice, thirdChoice }),
  })
}

// Photos (for public viewing)
export async function getPhotos() {
  return request<{
    photos: Photo[]
  }>('/api/photos')
}

// ============ ELO Voting ============

export interface EloPhoto {
  id: string
  photoTitle: string
  photoUrl: string
  grade: string
}

export interface EloStartResponse {
  sessionId: string
  photoA: EloPhoto
  photoB: EloPhoto
  progress: {
    completed: number
    total: number
  }
}

export interface EloVoteResponse {
  completed: boolean
  photoA?: EloPhoto
  photoB?: EloPhoto
  progress: {
    completed: number
    total: number
  }
}

export interface EloProgressResponse {
  sessionId: string
  comparisonsCompleted: number
  total: number
  completed: boolean
}

export async function startEloVoting() {
  return request<EloStartResponse>('/api/voting/elo/start', {
    method: 'POST',
  })
}

export async function submitEloVote(sessionId: string, winner: string, loser: string) {
  return request<EloVoteResponse>('/api/voting/elo/vote', {
    method: 'POST',
    body: JSON.stringify({ sessionId, winner, loser }),
  })
}

export async function getEloProgress(sessionId: string) {
  return request<EloProgressResponse>(`/api/voting/elo/progress/${sessionId}`)
}
